import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import {
    isValidLocationId,
    useLocationProviderConfig,
    useProviderSchemas,
} from '@/domain/providerConfig';

import { FeatureUnavailable } from '../FeatureUnavailable';

import { DataSourcesView } from './DataSourcesView';

export interface DataSourcesContainerProps {
    clientId: number;
    locationId: number;
}

function DataSourcesPage({
    locationId,
    isOptoScaleAdmin,
}: {
    locationId: number;
    isOptoScaleAdmin: boolean;
}) {
    const { t } = useTranslation();
    const config = useLocationProviderConfig(locationId);
    const schemas = useProviderSchemas();
    // react-query keeps the data when a later refetch fails (window focus, an expired
    // session): the page stays, with every card's unsaved edits, and only says so.
    const error =
        (config.data === null ? config.error : null) ??
        (schemas.schemas.length === 0 ? schemas.error : null);
    const refreshError = error === null ? (config.error ?? schemas.error) : null;
    // An id the query would never run for would otherwise leave the loader up for good.
    const unreadable = !isValidLocationId(locationId);

    if (error !== null || unreadable) {
        const forbidden = error?.kind === 'forbidden';
        // Asking again cannot help a refusal or an id that is not one.
        const canRetry = !forbidden && !unreadable;
        function retry(): void {
            if (config.data === null && config.error !== null) void config.refetch();
            if (schemas.schemas.length === 0 && schemas.error !== null) schemas.refetch();
        }
        return (
            <PageLayout title={t('providerConfig.page.title')}>
                <SomethingHappened
                    error={error}
                    title={t(
                        forbidden
                            ? 'providerConfig.errors.forbidden'
                            : 'providerConfig.page.loadFailed'
                    )}
                    onRetry={canRetry ? retry : undefined}
                    isRetrying={config.isFetching || schemas.isFetching}
                />
            </PageLayout>
        );
    }
    if (config.data === null || schemas.isLoading) return <Spinner size="lg" fullScreen />;
    return (
        <DataSourcesView
            config={config.data}
            schemas={schemas.schemas}
            isOptoScaleAdmin={isOptoScaleAdmin}
            refetch={config.refetch}
            refreshError={refreshError}
        />
    );
}

/**
 * The site's data sources. Only client admins get the page, and for now only OptoScale admins
 * reach it (OptoScaleAdminRoute): the backend guard decides, and this keeps everyone else from
 * a page of refused requests.
 */
function DataSourcesContainer({ clientId, locationId }: DataSourcesContainerProps) {
    const { isClientAdmin, isOptoScaleAdmin } = usePermissions();
    if (!isClientAdmin(clientId))
        return (
            <FeatureUnavailable
                linkTo={`/c/${clientId}/location/${locationId}`}
                reason="disabled"
            />
        );
    return <DataSourcesPage locationId={locationId} isOptoScaleAdmin={isOptoScaleAdmin} />;
}

export default DataSourcesContainer;
export { DataSourcesContainer };
